import React, { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { parser, parser_write, parser_end, default_renderer } from 'streaming-markdown';
import { Copy, FileText } from 'lucide-react';
import { toast } from 'react-hot-toast';
import 'katex/dist/katex.min.css';
import './MarkdownRenderer.css';

/**
 * Secure streaming content renderer for any content type following Chrome best practices
 * https://developer.chrome.com/docs/ai/render-llm-responses
 */
const StreamingContentRenderer = ({ 
  content, 
  isStreaming = false, 
  className = '', 
  onSecurityViolation,
  sources = [],
  agentMessageType,
  contentLanguage = 'markdown'
}) => {
  const [hasSecurityViolation, setHasSecurityViolation] = useState(false);
  const [renderableContent, setRenderableContent] = useState('');
  const previousContentRef = useRef('');
  const securityCheckTimeoutRef = useRef(null);
  
  // Refs for streaming markdown parser
  const containerRef = useRef(null);
  const markdownParserRef = useRef(null);
  const rendererRef = useRef(null);
  
  // Initialize streaming markdown parser
  useEffect(() => {
    if (containerRef.current && !markdownParserRef.current) {
      // Clear the container
      containerRef.current.innerHTML = '';
      
      // Create renderer and parser
      rendererRef.current = default_renderer(containerRef.current);
      markdownParserRef.current = parser(rendererRef.current);
    }
  }, []);
  
  // Filter out [CARDS] sections from content during streaming
  const filterCardsFromContent = (rawContent) => {
    if (!rawContent) return rawContent;
    
    // Remove [CARDS]...[/CARDS] sections including the content between them
    // Use case-insensitive regex to match the pattern and remove it entirely
    // Also handle partial cards during streaming
    let filtered = rawContent.replace(/\[CARDS\][\s\S]*?\[\/CARDS\]/gi, '');
    
    // Also remove incomplete/partial [CARDS] sections that might appear during streaming
    filtered = filtered.replace(/\[CARDS\][\s\S]*$/gi, '');
    
    return filtered.trim();
  };

  // Convert content using streaming markdown parser
  useEffect(() => {
    if (content !== previousContentRef.current) {
      // For streaming content, process immediately without debouncing
      // For complete content, we can add a small delay for final processing
      const processContent = () => {
        try {
          if (containerRef.current) {
            // Always reinitialize parser for clean state - this ensures consistent rendering
            containerRef.current.innerHTML = '';
            rendererRef.current = default_renderer(containerRef.current);
            markdownParserRef.current = parser(rendererRef.current);
            
            // Filter out card sections before processing markdown
            const filteredContent = filterCardsFromContent(content);
            
            // Process all content at once - streaming-markdown handles partial content well
            if (filteredContent) {
              parser_write(markdownParserRef.current, filteredContent);
              
              // If streaming is complete, finalize the parser
              if (!isStreaming) {
                parser_end(markdownParserRef.current);
              }
            }
            
            // Security check and sanitization
            const htmlContent = containerRef.current.innerHTML;
            const sanitized = DOMPurify.sanitize(htmlContent, {
              RETURN_DOM: false,
              RETURN_DOM_FRAGMENT: false,
              RETURN_DOM_IMPORT: false,
              SANITIZE_DOM: true,
              KEEP_CONTENT: true
            });
            
            // Check for security violations
            if (DOMPurify.removed && DOMPurify.removed.length > 0) {
              const dangerousElements = DOMPurify.removed.filter(item => {
                const element = item.element;
                if (!element) return false;
                
                const dangerousTags = ['script', 'iframe', 'object', 'embed', 'form', 'input'];
                const dangerousAttrs = ['onclick', 'onload', 'onerror', 'javascript'];
                
                const tagName = element.tagName?.toLowerCase();
                const innerHTML = element.innerHTML?.toLowerCase() || '';
                const outerHTML = element.outerHTML?.toLowerCase() || '';
                
                return dangerousTags.includes(tagName) || 
                       dangerousAttrs.some(attr => outerHTML.includes(attr)) ||
                       innerHTML.includes('javascript') ||
                       innerHTML.includes('script');
              });
              
              if (dangerousElements.length > 0) {
                console.warn('🚨 Security violation detected in streaming content:', dangerousElements);
                setHasSecurityViolation(true);
                onSecurityViolation?.(dangerousElements);
                return;
              }
            }
            
            // Update the container with sanitized content if needed
            if (sanitized !== htmlContent) {
              containerRef.current.innerHTML = sanitized;
            }
            
            setRenderableContent(filteredContent);
          }
          
        } catch (error) {
          console.error(`Failed to process markdown content:`, error);
          // Fallback to plain text
          const filteredContent = filterCardsFromContent(content);
          if (containerRef.current) {
            containerRef.current.innerHTML = `<p>${filteredContent}</p>`;
          }
          setRenderableContent(filteredContent);
        }
      };

      // Clear any pending timeout
      if (securityCheckTimeoutRef.current) {
        clearTimeout(securityCheckTimeoutRef.current);
      }

      if (isStreaming) {
        // For streaming content, process immediately for real-time updates
        processContent();
      } else {
        // For complete content, add tiny delay to batch final updates
        securityCheckTimeoutRef.current = setTimeout(processContent, 0);
      }
      
      previousContentRef.current = content;
    }
  }, [content, isStreaming, onSecurityViolation]);
  
  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (securityCheckTimeoutRef.current) {
        clearTimeout(securityCheckTimeoutRef.current);
      }
    };
  }, []);

  // Show security error if violation detected
  if (hasSecurityViolation) {
    return (
      <div className="border border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
        <div className="flex items-center">
          <svg className="h-5 w-5 text-red-400 dark:text-red-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Security Warning</h3>
            <p className="text-sm text-red-700 dark:text-red-300 mt-1">
              The AI response contained potentially unsafe content that was blocked for your security.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`streaming-content streaming-markdown-content ${className}`}>
        <div 
          ref={containerRef}
          className="content-body text-gray-900 dark:text-gray-100"
        />
        {/* Show action buttons only for 'content' type messages - these are final outputs worth copying/inserting */}
        {!isStreaming && agentMessageType === 'content' && (
          
          <div className='flex items-center justify-end'>
            <button
              onClick={() => {
                navigator.clipboard.writeText(renderableContent);
                toast.success('Message copied to clipboard!');
              }}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              title="Copy message"
            >
              <Copy size={15} />
            </button>
            
            <button
              onClick={() => {
                const event = new CustomEvent('insertTextIntoDocument', {
                  detail: { content: renderableContent, format: contentLanguage }
                });
                
                window.dispatchEvent(event);
                toast.success('Message inserted into document!');
              }}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              title="Insert into document"
            >
              <FileText size={15} />
            </button>
          </div>
        )}
      </div>
    </>
  );
};


StreamingContentRenderer.displayName = 'StreamingContentRenderer';

export default StreamingContentRenderer; 